import AddCircle from '@mui/icons-material/AddCircle';
import Cancel from '@mui/icons-material/Cancel';
import Edit from '@mui/icons-material/Edit';
import PlayArrow from '@mui/icons-material/PlayArrow';
import { useTheme } from '@mui/material';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import SvgIcon from '@mui/material/SvgIcon';
import { SxProps } from '@mui/system';
import { Subscription } from '@rails/actioncable';
import {
  ProjectEventName,
  SiteRateSchedule_Read_Nested,
} from '@treadinc/horizon-api-spec';
import dayjs from 'dayjs';
import { t } from 'i18next';
import { snakeCase } from 'lodash-es';
import { useEffect, useState } from 'react';

import {
  EVENT_TIMELINE_JOB_DATE_TIME_FORMAT,
  EVENT_TIMELINE_JOB_EVENT_TIME_FORMAT,
  EventTimeline,
  EventTimelineConnector,
  EventTimelineContentContainer,
  EventTimelineDot,
  EventTimelineIcon,
  EventTimelineItem,
  EventTimelinePrimaryText,
  EventTimelineSecondaryText,
  EventTimelineSeparator,
} from '~components/EventTimeline/EventTimeline';
import { Project } from '~hooks/useProjects/models';
import { ProjectEvent } from '~hooks/useProjects/projectEventModels';
import useProjectEvents from '~hooks/useProjects/useProjectEvents';
import { useStores } from '~store/RootStore';
import { getEffectiveUserCompanyId } from '~utils/user/user-utils';

import ProjectTimelineEventDetail, {
  getSiteRateScheduleSubject,
} from './ProjectTimelineEventDetail';

const SITE_RATE_SCHEDULE_EVENT_NAMES: ReadonlySet<ProjectEventName> = new Set([
  ProjectEventName.SITE_RATE_SCHEDULE_CREATED,
  ProjectEventName.SITE_RATE_SCHEDULE_UPDATED,
  ProjectEventName.SITE_RATE_SCHEDULE_DELETED,
]);

function getProjectEventSubject(event: ProjectEvent): string | null {
  if (!SITE_RATE_SCHEDULE_EVENT_NAMES.has(event.eventName)) return null;
  const payload = event.payload as
    | {
        site_rate_schedule?: SiteRateSchedule_Read_Nested | null;
        name?: string | null;
      }
    | null
    | undefined;
  return getSiteRateScheduleSubject(payload?.site_rate_schedule, payload?.name);
}

type DotVariant = {
  icon: typeof SvgIcon;
  color: string;
};

const FREIGHT_RATE_EVENT_LABEL_KEYS: Partial<Record<ProjectEventName, string>> = {
  [ProjectEventName.SITE_RATE_SCHEDULE_CREATED]:
    'project.timeline.events.freight_rates_created',
  [ProjectEventName.SITE_RATE_SCHEDULE_DELETED]:
    'project.timeline.events.freight_rates_deleted',
  [ProjectEventName.SITE_RATE_SCHEDULE_UPDATED]:
    'project.timeline.events.freight_rates_updated',
};

function getProjectEventLabel(event: ProjectEvent): string {
  const overrideKey = FREIGHT_RATE_EVENT_LABEL_KEYS[event.eventName];
  if (overrideKey) return t(overrideKey);

  return t(`project.timeline.events.${snakeCase(event.eventName)}`, {
    defaultValue: event.eventName,
  });
}

interface ProjectTimelineProps {
  project: Project;
  sx?: SxProps;
}

export default function ProjectTimeline({ project, sx }: ProjectTimelineProps) {
  const theme = useTheme();
  const dotByProjectEventName: Record<ProjectEventName, DotVariant> = {
    [ProjectEventName.PROJECT_CREATED]: {
      icon: AddCircle,
      color: theme.brandV2.colors.treadGreen,
    },
    [ProjectEventName.PROJECT_STATE_CHANGED]: {
      icon: PlayArrow,
      color: theme.brandV2.colors.treadBlue,
    },
    [ProjectEventName.MATERIAL_RATE_CREATED]: {
      icon: AddCircle,
      color: theme.brandV2.colors.treadGreen,
    },
    [ProjectEventName.MATERIAL_RATE_UPDATED]: {
      icon: Edit,
      color: theme.brandV2.colors.treadBlue,
    },
    [ProjectEventName.PROJECT_FREIGHT_DEFAULTS_UPDATED]: {
      icon: Edit,
      color: theme.brandV2.colors.treadBlue,
    },
    [ProjectEventName.SITE_RATE_SCHEDULE_CREATED]: {
      icon: AddCircle,
      color: theme.brandV2.colors.treadGreen,
    },
    [ProjectEventName.SITE_RATE_SCHEDULE_UPDATED]: {
      icon: Edit,
      color: theme.brandV2.colors.treadBlue,
    },
    [ProjectEventName.SITE_RATE_SCHEDULE_DELETED]: {
      icon: Cancel,
      color: theme.brandV2.colors.treadRed,
    },
    [ProjectEventName.TAX_RATE_SCHEDULE_SAVED]: {
      icon: Edit,
      color: theme.brandV2.colors.treadBlue,
    },
  };

  const { getAllProjectEvents, subscribeToProjectEventUpdates } = useProjectEvents();
  const { userStore } = useStores();
  const companyId = getEffectiveUserCompanyId(userStore);
  const [events, setEvents] = useState<ProjectEvent[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();

    const load = async () => {
      setIsLoading(true);
      try {
        const response = await getAllProjectEvents(
          project.id,
          companyId,
          controller.signal,
        );
        if (controller.signal.aborted) return;
        setEvents(response);
      } catch {
        // Toast is surfaced inside the hook; just clear loading state.
      } finally {
        if (!controller.signal.aborted) setIsLoading(false);
      }
    };

    void load();

    return () => controller.abort();
  }, [project.id, companyId, getAllProjectEvents]);

  useEffect(() => {
    if (!companyId || !project.id) return;

    const subscription: Subscription | undefined = subscribeToProjectEventUpdates({
      projectId: project.id,
      onMessageCallBack: (event) => {
        setEvents((current) => {
          const existingIndex = current.findIndex((e) => e.id === event.id);

          if (existingIndex !== -1) {
            const next = [...current];
            next[existingIndex] = event;
            return next;
          }

          return [event, ...current];
        });
      },
    });

    return () => {
      subscription?.unsubscribe();
    };
  }, [companyId, project.id, subscribeToProjectEventUpdates]);

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 2, ...sx }}>
        <CircularProgress size={20} />
      </Box>
    );
  }

  if (events.length === 0) {
    return (
      <Box sx={{ p: 2, ...sx }}>
        <EventTimelineSecondaryText>
          {t('project.timeline.no_events')}
        </EventTimelineSecondaryText>
      </Box>
    );
  }

  return (
    <Box sx={{ width: '100%', p: 1.5, ...sx }}>
      <EventTimeline>
        {events.map((event, index) => {
          const dot = dotByProjectEventName[event.eventName];
          const isLast = index === events.length - 1;

          return (
            <EventTimelineItem key={event.id ?? `${event.eventName}-${index}`}>
              <EventTimelineSeparator>
                <EventTimelineDot
                  innerColor={dot.color}
                  shape="icon"
                  sx={{ backgroundColor: dot.color, borderColor: dot.color }}
                >
                  <EventTimelineIcon
                    icon={dot.icon}
                    sx={{ color: 'white', width: '14px', height: '14px' }}
                  />
                </EventTimelineDot>

                {!isLast && <EventTimelineConnector />}
              </EventTimelineSeparator>

              <EventTimelineContentContainer hasDotIconAtLeft={Boolean(dot.icon)}>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: 3,
                    mt: '3px',
                    pt: '4px',
                  }}
                >
                  <Box sx={{ flex: 0, minWidth: '100px' }}>
                    <EventTimelinePrimaryText>
                      {dayjs
                        .tz(event.createdAt)
                        .format(EVENT_TIMELINE_JOB_EVENT_TIME_FORMAT)}
                    </EventTimelinePrimaryText>
                    <EventTimelineSecondaryText>
                      {dayjs
                        .tz(event.createdAt)
                        .format(EVENT_TIMELINE_JOB_DATE_TIME_FORMAT)}
                    </EventTimelineSecondaryText>
                  </Box>

                  <Box sx={{ flex: 0, minWidth: '140px' }}>
                    {event.createdByName && (
                      <EventTimelinePrimaryText>
                        {event.createdByName}
                      </EventTimelinePrimaryText>
                    )}
                  </Box>

                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <EventTimelinePrimaryText>
                      {getProjectEventLabel(event)}
                      {(() => {
                        const subject = getProjectEventSubject(event);
                        return subject ? (
                          <Box component="span" sx={{ ml: 0.5, fontWeight: 600 }}>
                            · {subject}
                          </Box>
                        ) : null;
                      })()}
                    </EventTimelinePrimaryText>
                    <ProjectTimelineEventDetail event={event} />
                  </Box>
                </Box>
              </EventTimelineContentContainer>
            </EventTimelineItem>
          );
        })}
      </EventTimeline>
    </Box>
  );
}
